/**
 * The holiday decorations of the shell screens (login, profile, room, results), drawn over
 * the page's backdrop by CSS and small inline SVGs — no canvas, no timers:
 *
 *  - Halloween: a big moon, cobwebs in the top corners with a spider swinging from one,
 *    jack-o'-lanterns in the bottom corners, bats crossing the sky;
 *  - winter: snow falling, a garland of lights twinkling along the top edge.
 *
 * Fixed layers that never take a tap. Everything is behind the screen (over the page's
 * backdrop: the panels and buttons hide what is behind them, so nothing covers text or a
 * control), except the garland along the very top edge, which is in front of the screen but
 * under dialogs, the connection banner and toasts. The room's settings gear sits in the
 * top-right corner: no moon or cobweb behind it there. What moves is a few small HTML elements
 * animating their transform or opacity (the compositor's work, no repaints), and nothing
 * moves with reduced motion (base.css): everything rests where it looks right. The screens'
 * own orange-purple or frosty accents and the backdrop (the glow of the pumpkins, a cold sky)
 * follow `<html data-season>`, in SeasonDecor.css. The game screen has its own decorations
 * (render/season*.ts).
 */
import type { ScreenId } from '../../app/screen';
import type { Holiday } from '../../season/season';
import './SeasonDecor.css';

export function SeasonDecor({ season, screen }: { season: Holiday; screen: ScreenId }) {
  return season === 'halloween' ? <HalloweenDecor screen={screen} /> : <WinterDecor />;
}

// ---- Halloween ----

/** Bats of the sky: where they rest with reduced motion, how long one crossing takes. */
const BATS = [
  { x: '14vw', y: '20vh', size: '40px', duration: '19s', delay: '-4s', flap: '0.32s' },
  { x: '57vw', y: '9vh', size: '30px', duration: '25s', delay: '-15s', flap: '0.26s' },
  { x: '78vw', y: '31vh', size: '35px', duration: '22s', delay: '-9s', flap: '0.3s' },
] as const;

function HalloweenDecor({ screen }: { screen: ScreenId }) {
  // The room's settings gear is in the top-right corner: the moon and a cobweb behind it
  // would take its contrast.
  const gearCorner = screen === 'room';
  return (
    <div class="season-decor season-decor--back season-decor--halloween" aria-hidden="true">
      {!gearCorner && <span class="season-moon" />}
      {BATS.map((bat) => (
        <span
          key={bat.x}
          class="season-bat"
          style={{
            '--x': bat.x,
            '--y': bat.y,
            '--size': bat.size,
            '--duration': bat.duration,
            '--delay': bat.delay,
            '--flap': bat.flap,
          }}
        >
          <Bat />
        </span>
      ))}
      <Cobweb class="season-web season-web--left" />
      {!gearCorner && <Cobweb class="season-web season-web--right" />}
      <span class="season-spider">
        <Spider />
      </span>
      <span class="season-pumpkin season-pumpkin--big">
        <Pumpkin />
      </span>
      <span class="season-pumpkin season-pumpkin--small">
        <Pumpkin />
      </span>
      <span class="season-pumpkin season-pumpkin--right">
        <Pumpkin />
      </span>
    </div>
  );
}

/** Spokes of a corner web (radians from the top edge towards the side) and its rings. */
const WEB_SPOKES = [3, 19, 36, 54, 71, 87].map((degrees) => (degrees * Math.PI) / 180);
const WEB_RINGS = [13, 26, 40, 55, 71, 88];

/** The web as one path, its corner at 0,0 (computed once). */
const WEB_PATH = (() => {
  const at = (r: number, a: number) =>
    `${(r * Math.cos(a)).toFixed(1)} ${(r * Math.sin(a)).toFixed(1)}`;
  let d = '';
  for (const a of WEB_SPOKES) d += `M0 0L${at(100, a)}`;
  for (const r of WEB_RINGS) {
    d += `M${at(r, WEB_SPOKES[0]!)}`;
    for (let i = 1; i < WEB_SPOKES.length; i++) {
      const from = WEB_SPOKES[i - 1]!;
      const to = WEB_SPOKES[i]!;
      // Every thread sags towards the corner between two spokes.
      const sag = r * Math.cos((to - from) / 2) * 0.8;
      d += `Q${at(sag, (from + to) / 2)} ${at(r, to)}`;
    }
  }
  return d;
})();

function Cobweb({ class: className }: { class: string }) {
  return (
    <svg class={className} viewBox="0 0 100 100" aria-hidden="true">
      <path class="season-web__threads" d={WEB_PATH} />
    </svg>
  );
}

/** A spider on its thread, hanging from the top of its box (it swings about that point). */
function Spider() {
  return (
    <svg viewBox="0 0 16 52" aria-hidden="true">
      <path class="season-web__threads" d="M8 0V36" />
      <path
        class="season-spider__legs"
        d="M5 38L-1 35-4 37M5 41H-2L-5 44M5 44L-1 46-3 50M6 46L2 50 1 54M11 38L17 35 20 37M11 41H18L21 44M11 44L17 46 19 50M10 46L14 50 15 54"
      />
      <ellipse class="season-spider__body" cx="8" cy="40.5" rx="5" ry="5.8" />
      <circle class="season-spider__body" cx="8" cy="48" r="3.3" />
      <circle class="season-spider__eye" cx="6.7" cy="48.5" r="0.9" />
      <circle class="season-spider__eye" cx="9.3" cy="48.5" r="0.9" />
    </svg>
  );
}

function Pumpkin() {
  return (
    <svg viewBox="0 0 64 58" aria-hidden="true">
      <path
        class="season-pumpkin__stem"
        d="M29.5 17c-.8-5 .9-9.4 5.6-12.4l2.6 3c-3 2-4 5.2-3.4 9.4z"
      />
      <g class="season-pumpkin__body">
        <ellipse cx="19" cy="37" rx="15" ry="17" />
        <ellipse cx="45" cy="37" rx="15" ry="17" />
        <ellipse class="season-pumpkin__middle" cx="32" cy="36" rx="13.5" ry="19" />
      </g>
      <path
        class="season-pumpkin__face"
        d="M16.5 31.5h9l-4.5-8zM38.5 31.5h9l-4.5-8zM29.6 38h4.8L32 33.8zM15 41.5Q32 59 49 41.5Q32 47.5 15 41.5z"
      />
      <path class="season-pumpkin__teeth" d="M25.5 44.2h4.2v3.6h-4.2zM34.3 44.2h4.2v3.6h-4.2z" />
    </svg>
  );
}

function Bat() {
  return (
    <svg viewBox="0 0 48 22" aria-hidden="true">
      <path d="M24 8.5C20 3.5 13 2 6 5c3 1 4 3 3 5.2C7 9 4 9 1 11c5 1 8 4 9 7.4 2-3 5-4.2 8-3.2 1-2 3-3 6-1.2 3-1.8 5-.8 6 1.2 3-1 6 .2 8 3.2 1-3.4 4-6.4 9-7.4-3-2-6-2-8-.8-1-2.2 0-4.2 3-5.2-7-3-14-1.5-18 3.5zM22 8.6l.5-3.4L24 7.2l1.5-2 .5 3.4z" />
    </svg>
  );
}

// ---- winter ----

/** Snowflakes: where they rest with reduced motion, their size, fall time and sway. */
const FLAKES = [
  { x: '3vw', y: '22vh', size: 8, duration: 12, delay: -2, sway: 14 },
  { x: '9vw', y: '64vh', size: 5, duration: 16, delay: -11, sway: 10 },
  { x: '15vw', y: '38vh', size: 10, duration: 10, delay: -6, sway: 18 },
  { x: '21vw', y: '84vh', size: 6, duration: 14, delay: -1, sway: 12 },
  { x: '27vw', y: '12vh', size: 5, duration: 17, delay: -9, sway: 9 },
  { x: '33vw', y: '55vh', size: 9, duration: 11, delay: -4, sway: 16 },
  { x: '39vw', y: '29vh', size: 6, duration: 15, delay: -13, sway: 11 },
  { x: '45vw', y: '74vh', size: 10, duration: 9, delay: -7, sway: 20 },
  { x: '51vw', y: '17vh', size: 5, duration: 18, delay: -3, sway: 8 },
  { x: '57vw', y: '47vh', size: 8, duration: 12, delay: -10, sway: 15 },
  { x: '63vw', y: '90vh', size: 6, duration: 13, delay: -5, sway: 13 },
  { x: '69vw', y: '33vh', size: 10, duration: 10, delay: -12, sway: 19 },
  { x: '75vw', y: '61vh', size: 5, duration: 16, delay: -8, sway: 10 },
  { x: '81vw', y: '7vh', size: 8, duration: 14, delay: -14, sway: 14 },
  { x: '87vw', y: '42vh', size: 6, duration: 15, delay: -6, sway: 12 },
  { x: '93vw', y: '78vh', size: 9, duration: 11, delay: -9, sway: 17 },
  { x: '98vw', y: '25vh', size: 5, duration: 17, delay: -4, sway: 9 },
] as const;

function WinterDecor() {
  return (
    <>
      {/* The snow falls behind the panels: it never passes over text or numbers. */}
      <div class="season-decor season-decor--back season-decor--winter" aria-hidden="true">
        {FLAKES.map((flake) => (
          <i
            key={flake.x}
            class="season-flake"
            style={{
              '--x': flake.x,
              '--y': flake.y,
              '--size': `${flake.size}px`,
              '--duration': `${flake.duration}s`,
              '--delay': `${flake.delay}s`,
              '--sway': `${flake.sway}px`,
            }}
          >
            <i class="season-flake__dot" />
          </i>
        ))}
      </div>
      <div class="season-decor season-decor--front season-decor--winter" aria-hidden="true">
        <Garland />
      </div>
    </>
  );
}

/** The lights of one period of the garland (four sags of its wire), in two groups. */
const BULBS = [
  { x: 24, color: '#ff4f64', group: 'a' },
  { x: 72, color: '#ffd84d', group: 'b' },
  { x: 120, color: '#4dff8f', group: 'a' },
  { x: 168, color: '#4dc8ff', group: 'b' },
] as const;
const GARLAND_PERIOD = 192;

/** The wire, then the two groups of lights: each group twinkles as a whole (its opacity). */
function Garland() {
  return (
    <span class="season-garland">
      <svg class="season-garland__layer" aria-hidden="true">
        <defs>
          <pattern
            id="season-garland-wire"
            width={GARLAND_PERIOD / 4}
            height="16"
            patternUnits="userSpaceOnUse"
          >
            <path class="season-garland__wire" d="M0 0.8Q24 10 48 0.8" />
          </pattern>
        </defs>
        <rect width="100%" height="16" fill="url(#season-garland-wire)" />
      </svg>
      <GarlandLights group="a" />
      <GarlandLights group="b" />
    </span>
  );
}

function GarlandLights({ group }: { group: 'a' | 'b' }) {
  const bulbs = BULBS.filter((bulb) => bulb.group === group);
  return (
    <svg
      class={`season-garland__layer season-garland__lights season-garland__lights--${group}`}
      aria-hidden="true"
    >
      <defs>
        {bulbs.map((bulb) => (
          <radialGradient key={bulb.x} id={`season-glow-${bulb.x}`}>
            <stop offset="0" stop-color={bulb.color} stop-opacity="0.7" />
            <stop offset="1" stop-color={bulb.color} stop-opacity="0" />
          </radialGradient>
        ))}
        <pattern
          id={`season-garland-${group}`}
          width={GARLAND_PERIOD}
          height="18"
          patternUnits="userSpaceOnUse"
        >
          {bulbs.map((bulb) => (
            <g key={bulb.x}>
              <circle cx={bulb.x} cy="9.5" r="7.5" fill={`url(#season-glow-${bulb.x})`} />
              <rect x={bulb.x - 1.8} y="4.4" width="3.6" height="2.6" rx="0.6" fill="#232a35" />
              <ellipse cx={bulb.x} cy="9.6" rx="2.7" ry="3.6" fill={bulb.color} />
              <ellipse cx={bulb.x - 0.9} cy="8.6" rx="0.8" ry="1.3" fill="#fff" opacity="0.75" />
            </g>
          ))}
        </pattern>
      </defs>
      <rect width="100%" height="18" fill={`url(#season-garland-${group})`} />
    </svg>
  );
}
